import { inject, Injectable, signal } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { FormsErrorHandlerService } from '../forms-error-handler.service';
import { FieldDescriptor, UserInfoService } from './user-info.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { Observable, of, ReplaySubject } from 'rxjs';
import { UserService } from './user.service';
import { ArrayService } from '../array.service';
import { ToastMessageService } from '../../modules/toast-message/toast-message.service';
import { UserFieldDescriptor, userFields, UserFieldType } from '../../helpers/user-fields.data';

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {
  private _formErrors = inject(FormsErrorHandlerService);
  private _userInfo = inject(UserInfoService);
  private _toastMessage = inject(ToastMessageService);
  private _user = inject(UserService);
  private _array = inject(ArrayService);


  /**
   * Is form loading
   */
  public loading = signal(false);

  public userProfileUpdated$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  public errors = {};

  public isFullDataFilled = signal(false);

  /**
   * Submit form handler
   */
  submit(form: FormGroup, context: string) {

    this._formErrors.applyFormErrors(form, null, true);

    if (form.invalid) {
      return;
    }

    const formValue = {...form.value};

    this.loading.set(true);

    this._userInfo.updatePlayerInfo(formValue, context).pipe(
      tap(() => {
        this._toastMessage.success('t.profile-updated');
        Object.keys(form.controls).forEach(key => {
          form.controls[key].disable();
        })
        this.isFullDataFilled.set(this.checkFullData(form));
      }),
      switchMap(() => this._user.fetchAllUserData()),
    ).subscribe(
      () => {
        this.loading.set(false);
        this.userProfileUpdated$.next(true);
      }, (error) => {
        this.loading.set(false);
        this.errors = this._formErrors.applyFormErrors(form, error.error, true);
        this.userProfileUpdated$.next(false);
      });
  }

  public resolveFields(context: string = 'edition'): Observable<(UserFieldDescriptor & FieldDescriptor)[]> {
    return this._userInfo.missingAttributesFor(context).pipe(
      map(missedFields => missedFields.map((field: FieldDescriptor) => {
        const options = Boolean(field?.inclusion?.in?.length && field?.field !== 'gender') ? {
          options: of(field.inclusion.in).pipe(
            map((data) => data.map(op => [op, op] as [string, string]))
          )
        } : {};
        return userFields.has(field.field) ? {
          ...field,
          ...userFields.get(field.field),
          ...options
        } : {
          ...field,
          type: field.type === 'boolean' ? UserFieldType.CHECKBOX : UserFieldType.TEXT,
          validators: [Validators.required],
          label: field.field,
          ...options
        };
      })),
    );
  }

  public checkFullData(group: FormGroup): boolean {
    return Object.keys(group.controls).every(key => group.controls[key].value)
  }
}
